import type { Cell, CellContext } from "@tanstack/react-table";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import merge from "lodash/merge";

import { Dinero, formatDineroCad } from "@/utils/dinero-util";
import { CostTaxBreakdown } from "../queries/tracking-data/cost-tax-breakdown";
import { CostTaxBreakdownErrors, CostTaxBreakdownErrorsPopover } from "./CostTaxBreakdownErrorsPopover";

export type ColumnDefOptions<TData> = Omit<DisplayColumnDef<TData, Dinero>, "id"> & { useErrorPopover?: boolean };

/** Gets three column definitions for the subtotal/tax/total of a `CostTaxBreakdown`. */
export function getCostTaxBreakdownColumnDefs<TData>(
  costAccessor: (row: TData) => CostTaxBreakdown,
  idPrefix: string,
  additionalColumnDef: ColumnDefOptions<TData> = {},
): ColumnDef<TData, Dinero>[] {
  // Create a new cellProps function, wrapping the provided one, that also sets some default values:
  // If there are errors, only the first visible column among the three will display the error and span all visible
  // columns. All other columns will be hidden.
  const cellProps = (cell: Cell<TData, Dinero>): React.TdHTMLAttributes<HTMLTableCellElement> => {
    // Get the cell props provided by the caller, if any
    const originalCellPropsOption = additionalColumnDef.meta?.cellProps;
    const originalCellProps =
      typeof originalCellPropsOption === "function" ? originalCellPropsOption(cell) : (originalCellPropsOption ?? {});

    // Return the default cellProps if there are no errors
    const cost = costAccessor(cell.row.original);
    if (!cost.errors.length) return originalCellProps;

    // Find the first visible column among the three (subtotal, tax, total)
    const colIds = [idPrefix + "_subtotal", idPrefix + "_tax", idPrefix + "_total"];
    const visibleColIds = cell.row.getVisibleCells().map((c) => c.column.id);
    const firstVisible = visibleColIds.find((id) => colIds.includes(id));

    if (firstVisible === cell.column.id) {
      // If this is the first visible column, set `colSpan` to span all the visible columns
      const numVisibleCols = colIds.filter((x) => visibleColIds.includes(x)).length;
      return { colSpan: numVisibleCols, ...originalCellProps };
    } else {
      // Otherwise, set `hidden` to hide the cell
      return { hidden: true, ...originalCellProps };
    }
  };

  // Merge the new cellProps callback into the provided column definition then use it to create the three column definitions
  const newColumnDef = merge({}, additionalColumnDef, { meta: { cellProps } });
  return [
    getCostTaxBreakdownSubtotalColumnDef(costAccessor, idPrefix, newColumnDef),
    getCostTaxBreakdownTaxColumnDef(costAccessor, idPrefix, newColumnDef),
    getCostTaxBreakdownTotalColumnDef(costAccessor, idPrefix, newColumnDef),
  ];
}

/** Gets a single column definition for the `subtotal` of a `CostTaxBreakdown` */
export function getCostTaxBreakdownSubtotalColumnDef<TData>(
  costAccessor: (row: TData) => CostTaxBreakdown,
  idPrefix: string,
  { useErrorPopover, ...additionalColumnDef }: ColumnDefOptions<TData> = {},
): ColumnDef<TData, Dinero> {
  return createColumnHelper<TData>().accessor((row: TData) => costAccessor(row).subtotal, {
    id: idPrefix + "_subtotal",
    header: "Subtotal",
    cell: getCostTaxBreakdownCellComponent(costAccessor, useErrorPopover),
    ...additionalColumnDef,
  });
}

/** Gets a single column definition for the `tax` of a `CostTaxBreakdown` */
export function getCostTaxBreakdownTaxColumnDef<TData>(
  costAccessor: (row: TData) => CostTaxBreakdown,
  idPrefix: string,
  { useErrorPopover, ...additionalColumnDef }: ColumnDefOptions<TData> = {},
): ColumnDef<TData, Dinero> {
  return createColumnHelper<TData>().accessor((row: TData) => costAccessor(row).tax, {
    id: idPrefix + "_tax",
    header: "Tax",
    cell: getCostTaxBreakdownCellComponent(costAccessor, useErrorPopover),
    ...additionalColumnDef,
  });
}

/** Gets a single column definition for the `total` of a `CostTaxBreakdown` */
export function getCostTaxBreakdownTotalColumnDef<TData>(
  costAccessor: (row: TData) => CostTaxBreakdown,
  idPrefix: string,
  { useErrorPopover, ...additionalColumnDef }: ColumnDefOptions<TData> = {},
): ColumnDef<TData, Dinero> {
  return createColumnHelper<TData>().accessor((row: TData) => costAccessor(row).total, {
    id: idPrefix + "_total",
    header: "Total",
    cell: getCostTaxBreakdownCellComponent(costAccessor, useErrorPopover),
    ...additionalColumnDef,
  });
}

/**
 * Gets a new component that renders the cell content for a cost/tax/total column. If there are errors, this renders the
 * error component in the cell. Otherwise, it renders the formatted value.
 */
function getCostTaxBreakdownCellComponent<TData>(
  costAccessor: (row: TData) => CostTaxBreakdown,
  useErrorPopover = false,
) {
  const CostTaxBreakDownCell: React.FC<CellContext<TData, Dinero>> = ({ row, getValue }) => {
    const cost = costAccessor(row.original);
    if (!cost.errors.length) return <>{formatDineroCad(getValue())}</>;
    return useErrorPopover ?
        <CostTaxBreakdownErrorsPopover cost={cost} />
      : <CostTaxBreakdownErrors errors={cost.errors} />;
  };
  return CostTaxBreakDownCell;
}
